import CircularProgress from "@material-ui/core/CircularProgress";
import MuiButton from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import CreateCustomDomainListModal from "../../../CampaignCreationCustomSlides/PublicationListSelectionSlide/components/CreateCustomDomainListModal/CreateCustomDomainListModal";
import EditCustomDomainListModal from "../../../CampaignCreationCustomSlides/PublicationListSelectionSlide/components/EditCustomDomainListModal/EditCustomDomainListModal";
import PublicationListsTable from "./PublicationListsTable";
import { BrandgenPublicationList } from "../../../../types/campaignTypes";
import {
  createDomainList,
  updateDomainList,
} from "../../../CampaignCreationCustomSlides/PublicationListSelectionSlide/publicationListApi";
import { usePublicationLists } from "../usePublicationLists";

// ─── Styled components ───────────────────────────────────────────────────────

const PanelsRow = styled("div")({
  display: "flex",
  gap: 16,
  alignItems: "flex-start",
});

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const CardHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const CardTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const CardBody = styled("div")({
  flex: 1,
  overflowY: "auto",
});

const CenteredMessage = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 40,
  color: theme.palette.brand.gray400,
}));

const PlaceholderText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray400,
}));

const CreateButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "6px 14px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  minWidth: "unset",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    transform: "translateY(-1px)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────

export type PublicationListsBrowseProps = {
  /** Render checkboxes so lists can be picked. Selection is fully controlled by the parent. */
  selectionMode?: boolean;
  /** Show the "Create List" button + per-row edit buttons for custom lists. */
  showListActions?: boolean;
  /** Max height of each panel body. Used to keep the browse usable inside a modal. */
  bodyMaxHeight?: number;
  selectedListIds?: string[];
  selectedDomainIds?: string[];
  onListSelect?: (listId: string) => void;
  onDomainSelect?: (domainId: string) => void;
};

/**
 * The two-panel publication list browser (BrandGen public lists | advertiser custom lists).
 * Owns fetching only — selection state belongs to whoever renders it, so the same component
 * serves the management page, the create/edit set modal, and the campaign builder panel.
 */
const PublicationListsBrowse: FC<PublicationListsBrowseProps> = ({
  selectionMode = false,
  showListActions = false,
  bodyMaxHeight,
  selectedListIds = [],
  selectedDomainIds = [],
  onListSelect,
  onDomainSelect,
}) => {
  const { selectedAdvertiserId, publicLists, customLists, isLoadingPublic, isLoadingCustom } = usePublicationLists();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [listToEdit, setListToEdit] = useState<BrandgenPublicationList | null>(null);

  const handleEditClick = (list: BrandgenPublicationList) => {
    setListToEdit(list);
    setIsEditModalOpen(true);
  };

  const bodyStyle = bodyMaxHeight ? { maxHeight: bodyMaxHeight } : undefined;

  return (
    <>
      <PanelsRow>
        {/* ── Public Lists ── */}
        <Card>
          <CardHeader>
            <CardTitle>BrandGen Publication Lists</CardTitle>
          </CardHeader>
          <CardBody style={bodyStyle}>
            {isLoadingPublic ? (
              <CenteredMessage>
                <CircularProgress size={28} />
              </CenteredMessage>
            ) : (
              <PublicationListsTable
                lists={publicLists}
                variant="public"
                isSelectionMode={selectionMode}
                isSplitMode={false}
                panelMode
                selectedListIds={selectedListIds}
                selectedDomainIds={selectedDomainIds}
                onListSelect={onListSelect}
                onDomainSelect={onDomainSelect}
              />
            )}
          </CardBody>
        </Card>

        {/* ── Custom Lists ── */}
        <Card>
          <CardHeader>
            <CardTitle>Custom Publication Lists</CardTitle>
            {showListActions && (
              <CreateButton disableElevation onClick={() => setIsCreateModalOpen(true)}>
                Create List
              </CreateButton>
            )}
          </CardHeader>
          <CardBody style={bodyStyle}>
            {isLoadingCustom ? (
              <CenteredMessage>
                <CircularProgress size={28} />
              </CenteredMessage>
            ) : !selectedAdvertiserId ? (
              <CenteredMessage>
                <PlaceholderText>Select an advertiser from the profile menu to view custom lists.</PlaceholderText>
              </CenteredMessage>
            ) : (
              <PublicationListsTable
                lists={customLists}
                variant="custom"
                isSelectionMode={selectionMode}
                isSplitMode={false}
                panelMode={!showListActions}
                selectedListIds={selectedListIds}
                selectedDomainIds={selectedDomainIds}
                onListSelect={onListSelect}
                onDomainSelect={onDomainSelect}
                onEditClick={showListActions ? handleEditClick : undefined}
              />
            )}
          </CardBody>
        </Card>
      </PanelsRow>

      {/* ── Modals ── */}
      {showListActions && (
        <>
          <CreateCustomDomainListModal
            isOpen={isCreateModalOpen}
            handleClose={() => setIsCreateModalOpen(false)}
            createDomainList={createDomainList}
          />
          <EditCustomDomainListModal
            isOpen={isEditModalOpen}
            handleClose={() => {
              setIsEditModalOpen(false);
              setListToEdit(null);
            }}
            customDomainListToEdit={listToEdit}
            updateDomainList={updateDomainList}
          />
        </>
      )}
    </>
  );
};

export default PublicationListsBrowse;
